export type PostHogAppEnvironment = 'production' | 'staging' | 'local' | 'unknown';

export interface PostHogEnvironmentProperties {
    app_environment: PostHogAppEnvironment;
    app_hostname: string;
    app_is_staging: boolean;
    app_node_env: string;
}

const PRODUCTION_HOSTNAMES = new Set([
    'ouronyx.com',
    'www.ouronyx.com',
    'prod.ouronyx.com',
    'ouronyx-production.netlify.app'
]);

const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0']);

const STAGING_HOSTNAMES = new Set([
    'ouronyx-uat.netlify.app',
    'ouronyx-s1.netlify.app',
    'ouronyx-s2.netlify.app',
    'ouronyx-s3.netlify.app',
    'ouronyx-s4.netlify.app'
]);

const STAGING_TOKEN_PATTERN = /^s[1-4](?:\.|$)/;

export const normalizePostHogHostname = (hostname?: string | null): string => {
    const rawHostname = String(hostname || '').trim().toLowerCase();
    if (!rawHostname) return '';

    try {
        if (/^https?:\/\//.test(rawHostname)) {
            return new URL(rawHostname).hostname.toLowerCase();
        }
    } catch {
        return rawHostname;
    }

    return rawHostname.split('/')[0].split(':')[0];
};

export const getPostHogAppEnvironment = (hostname?: string | null): PostHogAppEnvironment => {
    const normalizedHostname = normalizePostHogHostname(hostname);
    if (!normalizedHostname) return 'unknown';
    if (LOCAL_HOSTNAMES.has(normalizedHostname)) return 'local';
    if (PRODUCTION_HOSTNAMES.has(normalizedHostname)) return 'production';
    if (STAGING_HOSTNAMES.has(normalizedHostname)) return 'staging';
    if (STAGING_TOKEN_PATTERN.test(normalizedHostname)) return 'staging';
    if (
        normalizedHostname.endsWith('.netlify.app') &&
        (normalizedHostname.startsWith('deploy-preview-') || normalizedHostname.includes('--'))
    ) {
        return 'staging';
    }

    return 'unknown';
};

export const getPostHogEnvironmentProperties = (
    hostname?: string | null,
    nodeEnv?: string
): PostHogEnvironmentProperties => {
    const normalizedHostname = normalizePostHogHostname(hostname);
    const appEnvironment = getPostHogAppEnvironment(normalizedHostname);

    return {
        app_environment: appEnvironment,
        app_hostname: normalizedHostname || 'unknown',
        app_is_staging: appEnvironment === 'staging',
        app_node_env: nodeEnv || 'unknown'
    };
};

const getCurrentHostname = (): string => {
    if (typeof window === 'undefined') return '';
    return window.location.hostname;
};

export const getCurrentPostHogEnvironmentProperties = (): PostHogEnvironmentProperties =>
    getPostHogEnvironmentProperties(getCurrentHostname(), process.env.NODE_ENV);

export const withPostHogEnvironmentProperties = (
    properties?: Record<string, unknown>,
    hostname?: string | null,
    nodeEnv?: string
): Record<string, unknown> => ({
    ...properties,
    ...getPostHogEnvironmentProperties(hostname || getCurrentHostname(), nodeEnv || process.env.NODE_ENV)
});
